import { useQuery } from '@tanstack/react-query'
import epsClient from 'enrollment-platform/api/eps.client'
import { useMemo } from 'react'
import type { TemplateType } from '../../../models/templateType'
import type { MappingRule } from '../../CreateCensusTemplate/types'
import { importRulesJSON } from '../../CreateCensusTemplate/utils/transformer'

interface HrisMappingTemplateEntry {
  id: string
  type?: TemplateType
  name?: string
  status?: string
  mappingRules: {
    schema_version: string
    mapping_rules: unknown[]
    computed_fields?: unknown[]
  }
}

export interface ActiveHrisMappingTemplate {
  id: string
  type: TemplateType
  name: string
  mappingRules: MappingRule[]
  computedFields: MappingRule[]
}

function isActiveStatus(status: string | undefined): boolean {
  return (status ?? '').trim().toUpperCase() === 'ACTIVE'
}

// Fetches this client's HRIS mapping templates and returns only the
// currently-active one(s) — a client can have at most one active template
// per channel (AMA, API_INTEGRATION), so there may be 0, 1, or 2 results.
export default function useActiveHrisMappingTemplates(clientPublicKey: string) {
  const { data, isLoading } = useQuery<HrisMappingTemplateEntry[]>({
    queryKey: ['hris-mapping-templates', clientPublicKey],
    queryFn: async () => {
      const result = await epsClient.get(
        `/api/v1/hris-mapping-templates/client/${clientPublicKey}`
      )
      return result.data
    },
    enabled: !!clientPublicKey,
  })

  const activeTemplates = useMemo<ActiveHrisMappingTemplate[]>(() => {
    return (data ?? [])
      .filter((template) => isActiveStatus(template.status))
      .map((template) => {
        const imported = importRulesJSON(JSON.stringify(template.mappingRules))
        return {
          id: template.id,
          type: template.type ?? 'AMA',
          name: template.name || template.type || 'HRIS Mapping Template',
          mappingRules: imported.mappingRules,
          computedFields: imported.computedFields,
        }
      })
  }, [data])

  return { activeTemplates, isLoading }
}
